<template>
  <b-overlay :show="loading" opacity="0.5">
    <add-consultation-modal
      :editing-data.sync="editingData"
      :is-add-active.sync="toggleModal"
      resource="consultations"
      modal-title="Consultation"
      @create="onCreate"
      @refetchData="fetchAllData"
    />

    <div>
      <list-table
        :records="records" 
        :columns="tableColumns"
        :show-import-button="false"
        :total-records="totalRecords"
        :search-filter.sync="searchFilter"
        search-place-holder="Search Patient"
        :current-page-number.sync="currentPage"
        :show-export-button="can('consultations:CanExport')"
        @export="onExport"
        @row-clicked="onViewSummary"
      >
        <template v-if="can('consultations:CanAddNew')" #action_button>
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="primary"
            @click="onAddNew"
          >
            <span class="align-middle text-lg">Add new consultation</span>
          </b-button>
        </template>

        <template #cell(patient)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :src="getValueFromSource(data.item, 'patient.avatar.path')"
                :text="sentenceCase(avatarText(`${getValueFromSource(data.item, 'patient.full_name')}`))"
                :variant="`light-success`"
                :to="{ name: 'admin-patient-details', params: { id: getValueFromSource(data.item, 'patient._id') } }"
              />
            </template>

            <b-link
              :to="{ name: 'admin-patient-details', params: { id: getValueFromSource(data.item, 'patient._id') } }"
              class="font-weight-bold d-block text-nowrap"
            >
                {{ sentenceCase(getValueFromSource(data.item, 'patient.full_name')) }}
            </b-link>

            <span class="text-muted">{{ getValueFromSource(data.item, 'patient.email') }}</span><br />
            <span class="text-muted">@{{ getValueFromSource(data.item, 'patient.phone') }}</span>
          </b-media>
        </template>

        <template #cell(doctor)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :src="getValueFromSource(data.item, 'doctor.avatar.path')"
                :text="sentenceCase(avatarText(`${getValueFromSource(data.item, 'doctor.full_name')}`))"
                :variant="`light-success`"
                :to="{ name: 'admin-single', params: { id: getValueFromSource(data.item, 'doctor._id') } }"
              />
            </template>

            <b-link
              :to="{ name: 'admin-single', params: { id: getValueFromSource(data.item, 'doctor._id') } }"
              class="font-weight-bold d-block text-nowrap"
            >
                {{ sentenceCase(getValueFromSource(data.item, 'doctor.full_name')) }}
            </b-link>

            <span class="text-muted">{{ getValueFromSource(data.item, 'doctor.email') }}</span><br />
          </b-media>
        </template>

        <template #cell(consultation_type)="data">
          <b-badge
            pill
            :variant="`light-${resolveStatusVariant(data.item.consultation_type)}`"
            class="text-capitalize"
          >
            {{ getUserStatusText(data.item.consultation_type) }}
          </b-badge>
        </template>

        <template #cell(status)="data">
          <b-badge
            pill
            :variant="`light-${resolveStatusVariant(data.item.status)}`"
            class="text-capitalize"
          >
            {{ getUserStatusText(data.item.status) }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            
            <b-dropdown-item @click="onViewSummary(data.item)">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">View</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </list-table>
    </div>
  </b-overlay>
</template>

<script>
import {
  BOverlay, BCard, BRow, BCol, BAlert, BLink, BTable,
  BMedia, BAvatar, BButton, BFormFile,
  BBadge, BDropdown, BDropdownItem, BPagination,
  BInputGroup, BInputGroupAppend, BFormInput
} from 'bootstrap-vue'

import { get, debounce, startCase } from 'lodash';

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import FileDownload from "js-file-download";

import ListTable from "@/@core/components/ListTable/ListTable.vue";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AddConsultationModal from '@/@core/components/shared/admin/modals/AddConsultationModal.vue';

const watchHandler = {
  handler(){
    this.debouncedFetchUsersData()
  }, 
  immediate: false
}

const watchHandlerAndResetPageNumber = {
  handler(){
    this.currentPage = 1
    this.debouncedFetchUsersData()
  }, 
  immediate: false
}


export default {
  components: {
    ListTable,

    BOverlay,
    BCard,
    BTable,
    BRow,
    BCol,
    BAlert,
    BBadge,
    BLink,
    BMedia,
    BAvatar,
    BButton,
    BDropdown,
    BFormFile,
    BFormInput,
    BPagination,
    BInputGroup, 
    BDropdownItem,
    BInputGroupAppend, 

    vSelect,
    AddConsultationModal
  },
  directives: {
    Ripple,
  },
  data(){
    return {
      loading: false,
      showFilter: false,
      toggleModal: false,

      editingData: null,
      searchFilter: null,

      records: [],
      
      tableColumns: [
        { key: 'patient', label: 'Patient', sortable: false, },
        { key: 'doctor', label: 'Medical Professional', sortable: false, formatter: startCase },
        { key: 'consultation_type', label: 'Type', sortable: false, formatter: startCase },
        { key: 'lab', sortable: false, formatter: () => '-' },
        { key: 'pharmacy', sortable: false, formatter: () => '-' },
        { key: 'summary', sortable: false, formatter: () => '-' },
        { key: 'status' },
        { key: 'created', label: 'Date', sortable: false, formatter: val => `${this.formatDate(val)}`, },
        { key: 'actions' },
      ],
      debouncedFetchUsersData: () => {}
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    perPage: {
      handler(){
        this.currentPage = 1;
        this.debouncedFetchUsersData()
      }, 
      immediate: false
    },
    currentPage: watchHandler,
    searchFilter: watchHandlerAndResetPageNumber,
  },
  created(){
    this.debouncedFetchUsersData = debounce(this.fetchAllData, 500);
    this.fetchAllData()

    const { 
      page = 1, 
      search = "", 
      limit = this.perPage 
    } = this.$route.query;

    this.currentPage = +page;
    this.searchFilter = search;
    this.perPage = +limit;
  },
  methods: {
    async fetchAllData() {
      try {
        this.loading = true; 

        const query = {
          limit: this.perPage,
          page: this.currentPage,
          search: this.searchFilter,
        }
        this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.consultations.list(query);
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords
        this.perPage = pagination.limit;
        this.records = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onExport(){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const query = {
          search: this.searchFilter,
        }

        const response = await this.useJwt().mainService.consultations.export(query);
        FileDownload(response.data, "consultations.csv");
      } catch (error) {
        const error_message = get(error, "response.statusText") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    async onCreate(form_data){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: "Caution!!!",
          text: `Proceed to add new record?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: 'Yes',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.isConfirmed) {
          return;
        }

        const { payload, save_and_proceed } = form_data;
        const response = await this.useJwt().mainService.consultations.create(payload);
        this.fetchAllData();

        if (save_and_proceed){
          this.toggleModal = false;
          const id = get(response, 'data.data.id');
          if (id){
            this.$router.push({
              params: { id },
              name: 'admin-consultation-summary',
            })
          }
        } else {
          this.toggleModal = false;
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Record added successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    onAddNew(){
      this.editingData = null;
      this.toggleModal = true;
    },
    onViewSummary(consultation){
      if (consultation.consultation_type === 'dietary'){
        this.$router.push({
        name: 'admin-dietary-consultation-summary',
        params: { id: consultation.id }
      })
      } else {
        this.$router.push({
        name: 'admin-medical-consultation-summary',
        params: { id: consultation.id }
      })
      }
    },
    
  }
}
</script>

<style lang="scss" scoped>
  @import '@core/scss/vue/libs/vue-select.scss';
  .width-100 {
    width: 100px;
  }
  .per-page-selector {
    width: 90px;
  }
</style>

<style lang="scss">
  .loan-list-tb-row {
    cursor: pointer !important;
  }
</style>
